import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Copy, Check, ChevronRight, Loader, TrendingUp, Gift, Crown, Star, Zap, Users, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

// ── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const EVENT_COLORS = {
  earn:   'text-emerald-600',
  redeem: 'text-red-500',
  expire: 'text-red-400',
  adjust: 'text-blue-500',
};

// ─── Section 1: Wallet Summary ───────────────────────────────────────────────
function WalletSummary({ balance, lifetimeEarned, dollarValue, nextMilestone, progressPct }) {
  return (
    <div className="rounded-2xl p-6 text-white mb-6" style={{ background: 'linear-gradient(160deg,#041e2b,#094f6f)' }}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-1">Your RentalX Rewards</p>
          <div className="flex items-end gap-2">
            <span className="text-5xl font-black">{fmt(balance)}</span>
            <span className="text-white/60 text-lg mb-1">pts</span>
          </div>
          <p className="text-[#2cbdc4] font-semibold mt-1">= A${dollarValue} in rewards</p>
        </div>
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(44,189,196,0.2)' }}>
          <Trophy className="w-7 h-7 text-[#2cbdc4]" />
        </div>
      </div>

      {/* Progress to next milestone */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-white/60 mb-1">
          <span>Progress to next {fmt(nextMilestone)} pts milestone</span>
          <span>{progressPct}%</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%`, background: BRAND }}
          />
        </div>
        <p className="text-xs text-white/40 mt-1">
          {fmt(nextMilestone - (balance % nextMilestone || nextMilestone))} pts to next redemption · Lifetime: {fmt(lifetimeEarned)} pts
        </p>
      </div>
    </div>
  );
}

// ─── Section 2: Redemption ───────────────────────────────────────────────────
const STUDENT_REDEMPTIONS = [
  { type: 'voucher_10',    pts: 10_000, label: '$10 Gift Voucher',      sub: 'Prezzee gift card',             icon: Gift  },
  { type: 'free_month',   pts: 10_000, label: '1 Month Free',           sub: 'Subscription credit',           icon: Crown },
  { type: 'vibemate_plus',pts: 20_000, label: 'VibeMate Plus',          sub: '1 month free upgrade',          icon: Zap   },
];
const LANDLORD_REDEMPTIONS = [
  { type: 'voucher_10',       pts: 10_000, label: '$10 Gift Voucher',        sub: 'Prezzee gift card',              icon: Gift  },
  { type: 'free_month',       pts: 10_000, label: '1 Month Free',            sub: 'Subscription credit',            icon: Crown },
  { type: 'featured_listing', pts: 20_000, label: 'Featured Listing Boost',  sub: '7 days top of search results',   icon: Star  },
];

function RedemptionSection({ balance, token, userRole, onRedeemed }) {
  const options = userRole === 'landlord' ? LANDLORD_REDEMPTIONS : STUDENT_REDEMPTIONS;
  const [loading, setLoading] = useState(null);
  const [msg, setMsg] = useState('');

  const redeem = async (type, pts) => {
    if (balance < pts) return;
    setLoading(type);
    try {
      const r = await fetch(`${API}/api/rewards/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ redemption_type: type }),
      });
      const d = await r.json();
      if (!r.ok) { setMsg(d.detail || 'Redemption failed'); }
      else { setMsg(d.message); onRedeemed(); }
    } catch { setMsg('Network error. Please try again.'); }
    setLoading(null);
  };

  return (
    <div className="mb-6">
      <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
        <Gift className="w-4 h-4 text-[#094f6f]" /> Redeem Points
      </h3>
      {msg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">{msg}</div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {options.map((opt) => {
          const canRedeem = balance >= opt.pts;
          const Icon = opt.icon;
          return (
            <div key={opt.type}
              className={`rounded-2xl p-4 border transition-all ${canRedeem ? 'border-[#094f6f]/30 bg-white hover:shadow-md' : 'border-gray-200 bg-gray-50 opacity-60'}`}
              data-testid={`redeem-${opt.type}`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: canRedeem ? BRAND : '#e5e7eb' }}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <p className="font-bold text-gray-900 text-sm">{opt.label}</p>
              <p className="text-xs text-gray-500 mb-2">{opt.sub}</p>
              <p className="text-lg font-black text-[#094f6f] mb-3">{fmt(opt.pts)} pts</p>
              <button
                onClick={() => redeem(opt.type, opt.pts)}
                disabled={!canRedeem || loading === opt.type}
                className="w-full py-2 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-40 flex items-center justify-center gap-1"
                style={{ background: BRAND }}
              >
                {loading === opt.type ? <Loader className="w-3 h-3 animate-spin" /> : null}
                {loading === opt.type ? 'Processing…' : canRedeem ? `Redeem for ${opt.label}` : `Need ${fmt(opt.pts - balance)} more pts`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Section 3: History ──────────────────────────────────────────────────────
function PointsHistory({ token }) {
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const PER_PAGE = 20;

  useEffect(() => {
    let active = true;
    async function loadTxs() {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/rewards/history?page=${page}&per_page=${PER_PAGE}`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        if (active) { setTxs(d.transactions || []); setTotal(d.total || 0); }
      } catch (_e) { /* silent */ }
      if (active) setLoading(false);
    }
    loadTxs();
    return () => { active = false; };
  }, [token, page]);

  if (loading) return <div className="py-8 text-center text-gray-400 text-sm">Loading history…</div>;

  return (
    <div>
      <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-[#094f6f]" /> Points History
      </h3>
      {txs.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No transactions yet. Start earning points!</p>
      ) : (
        <>
          <div className="rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Event</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Points</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((tx, i) => {
                  const isExpired = tx.type === 'expire';
                  const pts = tx.points || 0;
                  return (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(tx.created_at)}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {tx.event_label || tx.description}
                        {isExpired && <span className="ml-2 text-xs font-semibold text-red-400 bg-red-50 px-1.5 py-0.5 rounded">Expired</span>}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold whitespace-nowrap ${pts > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {pts > 0 ? '+' : ''}{fmt(pts)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {total > PER_PAGE && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-50">
                ← Prev
              </button>
              <span className="text-gray-500">Page {page} of {Math.ceil(total / PER_PAGE)}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / PER_PAGE)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-50">
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Section 4: Refer & Earn ─────────────────────────────────────────────────
function ReferralSection({ token }) {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/rewards/referral`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setInfo).catch(() => {});
  }, [token]);

  const copy = async () => {
    if (!info?.referral_link) return;
    await navigator.clipboard.writeText(info.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const EARN_TABLE = [
    { tier: 'Student',      pts: '1,000' },
    { tier: 'Professional', pts: '2,500' },
    { tier: 'Landlord',     pts: '5,000' },
  ];

  return (
    <div className="mt-6">
      <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
        <Users className="w-4 h-4 text-[#094f6f]" /> Refer & Earn
      </h3>

      {/* Referral link */}
      {info?.referral_link && (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 mb-4">
          <p className="text-xs text-gray-500 mb-2 font-medium">Your unique referral link</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-sky-200 rounded-xl px-3 py-2 text-xs text-[#094f6f] font-mono truncate">
              {info.referral_link}
            </code>
            <button onClick={copy} data-testid="copy-referral-link"
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90"
              style={{ background: BRAND }}>
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Earnings table */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {EARN_TABLE.map(row => (
          <div key={row.tier} className="bg-white border border-gray-100 rounded-xl p-3 text-center shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Refer a {row.tier}</p>
            <p className="font-black text-[#094f6f]">{row.pts} pts</p>
          </div>
        ))}
      </div>

      {/* Referral list */}
      {info?.referrals?.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Your Referrals</p>
          {info.referrals.map((r, i) => (
            <div key={i} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-800">{r.referred_first_name}</p>
                <p className="text-xs text-gray-400 capitalize">{r.referred_tier} · {fmtDate(r.created_at)}</p>
              </div>
              {r.status === 'confirmed' ? (
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">+{fmt(r.points_awarded)} pts</span>
              ) : (
                <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Pending first payment</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main RewardsTab ─────────────────────────────────────────────────────────
export default function RewardsTab({ token, userRole = 'student' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    async function loadBalance() {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/rewards/balance`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        if (active) setData(d);
      } catch (_e) { /* silent */ }
      if (active) setLoading(false);
    }
    loadBalance();
    return () => { active = false; };
  }, [token, refreshKey]);

  const load = useCallback(() => setRefreshKey(k => k + 1), []);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader className="w-8 h-8 animate-spin text-[#094f6f]" />
    </div>
  );

  const balance      = data?.balance ?? 0;
  const lifetime     = data?.lifetime_earned ?? 0;
  const dollarVal    = data?.dollar_value ?? 0;
  const nextMile     = data?.next_milestone ?? 10000;
  const progressPct  = data?.progress_pct ?? 0;

  return (
    <div className="space-y-2" data-testid="rewards-tab">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold text-gray-900">Rewards</h2>
        <button onClick={load} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <WalletSummary
        balance={balance}
        lifetimeEarned={lifetime}
        dollarValue={dollarVal}
        nextMilestone={nextMile}
        progressPct={progressPct}
      />

      {balance >= 10000 && (
        <RedemptionSection
          balance={balance}
          token={token}
          userRole={userRole}
          onRedeemed={load}
        />
      )}

      <PointsHistory token={token} />
      <ReferralSection token={token} />
    </div>
  );
}

// ─── Compact badge for dashboard headers ────────────────────────────────────
export function PointsBadge({ token, onClick }) {
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/rewards/balance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setBalance(d.balance)).catch(() => {});
  }, [token]);

  if (balance === null) return null;

  return (
    <button
      onClick={onClick}
      data-testid="points-badge"
      className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
    >
      <Trophy className="w-3.5 h-3.5" />
      {Number(balance).toLocaleString()} pts
    </button>
  );
}
